import { useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Clock,
  ChevronDown, ChevronLeft, ChevronRight,
  Briefcase, FileText, Save, Coffee, Palmtree, Printer,
  TrendingUp, Sun, ThermometerSun,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/admin';
import { Button, Card, Badge, Spinner, Input, Select } from '@/components/common';
import { users as usersApi } from '@/utils/api';
import { useUser, useYearOverview, useStammblatt } from '@/hooks/useUsers';
import { toast } from 'sonner';

// Helpers
const germanDateToISO = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.includes('-')) return dateStr;
  const parts = dateStr.split('.');
  if (parts.length !== 3) return '';
  const day = parts[0].padStart(2, '0');
  const month = parts[1].padStart(2, '0');
  let year = parts[2];
  if (year.length === 2) {
    year = parseInt(year) < 50 ? `20${year}` : `19${year}`;
  }
  return `${year}-${month}-${day}`;
};

const isoToGermanDate = (isoStr) => {
  if (!isoStr) return '';
  if (isoStr.includes('.')) return isoStr;
  const parts = isoStr.split('-');
  if (parts.length !== 3) return isoStr;
  return `${parts[2]}.${parts[1]}.${parts[0].slice(-2)}`;
};

const formatMinutes = (min) => {
  if (!min || min === 0) return '0:00';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

const formatMinutesShort = (min) => {
  if (!min || min === 0) return '0h';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const getWeekday = (dateStr) => {
  const [dd, mm, yy] = dateStr.split('.').map(Number);
  const fullYear = yy < 50 ? 2000 + yy : 1900 + yy;
  const d = new Date(fullYear, mm - 1, dd);
  return ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][d.getDay()];
};

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

// ========== DETAILS TAB ==========
const DetailsTab = ({ userId }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [expandedMonth, setExpandedMonth] = useState(() => new Date().getMonth() + 1);

  const { data, isLoading } = useYearOverview(userId, year);

  // Reset expanded month when year changes
  const handleYearChange = (delta: number) => {
    setYear(y => {
      const next = y + delta;
      setExpandedMonth(next === new Date().getFullYear() ? new Date().getMonth() + 1 : null);
      return next;
    });
  };

  if (isLoading) return <Spinner.Center />;
  if (!data) return <p className="text-gray-500 text-center py-8">Keine Daten verfügbar</p>;

  // Jahres-Zusammenfassung
  const monthTotals = data.months.reduce((acc, m) => ({
    daysPresent: acc.daysPresent + m.daysPresent + m.weekendDays,
    actualMinutes: acc.actualMinutes + m.actualMinutes,
    monthTargetMinutes: acc.monthTargetMinutes + m.targetMinutes,
    overtimeMinutes: acc.overtimeMinutes + m.overtimeMinutes,
    sickDays: acc.sickDays + m.sickDays,
    vacationDays: acc.vacationDays + m.vacationDays,
  }), { daysPresent: 0, actualMinutes: 0, monthTargetMinutes: 0, overtimeMinutes: 0, sickDays: 0, vacationDays: 0 });

  // Jahres-Soll: Werktage - Feiertage - Urlaubsanspruch (vom Backend berechnet)
  const yearTotals = {
    ...monthTotals,
    targetMinutes: data.yearTargetMinutes ?? monthTotals.monthTargetMinutes,
  };

  return (
    <div>
      {/* Jahr-Auswahl + Zusammenfassung */}
      <Card className="mb-6">
        <Card.Content>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <button onClick={() => handleYearChange(-1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <span className="text-lg font-bold text-gray-800 min-w-[60px] text-center">{year}</span>
              <button onClick={() => handleYearChange(1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <Badge variant={data.employee.type === 'TZ' ? 'warning' : 'info'}>
              {data.employee.type === 'TZ' ? 'Teilzeit' : 'Vollzeit'} · {data.employee.dailyTargetMinutes / 60}h/Tag
            </Badge>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <p className="text-xs text-blue-600 font-medium">Anwesend</p>
              <p className="text-xl font-bold text-blue-700">{yearTotals.daysPresent}</p>
              <p className="text-xs text-blue-500">Tage</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-600 font-medium">Soll</p>
              <p className="text-xl font-bold text-gray-700">{formatMinutes(yearTotals.targetMinutes)}</p>
              <p className="text-xs text-gray-500">Stunden</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-3 text-center">
              <p className="text-xs text-emerald-600 font-medium">Ist</p>
              <p className="text-xl font-bold text-emerald-700">{formatMinutes(yearTotals.actualMinutes)}</p>
              <p className="text-xs text-emerald-500">Stunden</p>
            </div>
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <p className="text-xs text-green-600 font-medium">Überstunden</p>
              <p className="text-xl font-bold text-green-700">+{formatMinutes(yearTotals.overtimeMinutes)}</p>
              <p className="text-xs text-green-500">gesamt</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-3 text-center">
              <p className="text-xs text-yellow-600 font-medium">Krank</p>
              <p className="text-xl font-bold text-yellow-700">{yearTotals.sickDays}</p>
              <p className="text-xs text-yellow-500">Tage</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-3 text-center">
              <p className="text-xs text-purple-600 font-medium">Urlaub</p>
              <p className="text-xl font-bold text-purple-700">{yearTotals.vacationDays}</p>
              <p className="text-xs text-purple-500">Tage</p>
            </div>
          </div>
        </Card.Content>
      </Card>

      {/* Monatskarten */}
      <div className="space-y-3">
        {data.months.map((m) => {
          const isExpanded = expandedMonth === m.month;
          const hasDays = m.days.length > 0 || m.sickDays > 0 || m.vacationDays > 0;
          const hasData = m.daysPresent > 0 || m.weekendDays > 0 || m.sickDays > 0 || m.vacationDays > 0;
          const isCurrentMonth = year === new Date().getFullYear() && m.month === new Date().getMonth() + 1;

          return (
            <div key={m.month} className={`bg-white rounded-xl border ${isCurrentMonth ? 'border-blue-300 shadow-sm' : 'border-gray-100'} overflow-hidden`}>
              {/* Monat Header — klickbar */}
              <button
                onClick={() => setExpandedMonth(isExpanded ? null : m.month)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                disabled={!hasDays}
              >
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-semibold ${hasData ? 'text-gray-800' : 'text-gray-400'}`}>
                    {MONTH_NAMES[m.month - 1]}
                  </span>
                  {isCurrentMonth && (
                    <span className="text-[10px] bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-medium">aktuell</span>
                  )}
                </div>

                <div className="flex items-center gap-3 sm:gap-5">
                  {/* Kompakte Monats-Stats */}
                  {hasData ? (
                    <>
                      <div className="hidden sm:flex items-center gap-1 text-xs text-gray-500" title="Anwesenheitstage">
                        <Briefcase className="w-3.5 h-3.5" />
                        <span>{m.daysPresent}{m.weekendDays > 0 ? `+${m.weekendDays}` : ''}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs" title={`Soll: ${formatMinutes(m.targetMinutes)} / Ist: ${formatMinutes(m.actualMinutes)}`}>
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-gray-500">{formatMinutes(m.actualMinutes)}</span>
                        <span className="text-gray-300">/</span>
                        <span className="text-gray-400">{formatMinutes(m.targetMinutes)}</span>
                      </div>
                      {m.overtimeMinutes > 0 && (
                        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                          +{formatMinutes(m.overtimeMinutes)}
                        </span>
                      )}
                      {m.sickDays > 0 && (
                        <span className="text-xs font-medium text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded-full" title="Krankentage">
                          {m.sickDays}K
                        </span>
                      )}
                      {m.vacationDays > 0 && (
                        <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full" title="Urlaubstage">
                          {m.vacationDays}U
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-gray-300">keine Einträge</span>
                  )}
                  {hasDays && (
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                  )}
                </div>
              </button>

              {/* Aufgeklappte Tages-Details */}
              {isExpanded && (m.days.length > 0 || m.sickDates?.length > 0 || m.vacationDates?.length > 0) && (() => {
                // Alle Tage zusammenführen (Zeiteinträge + Krank + Urlaub)
                const allDays = [...m.days.map(d => ({ ...d, dayType: 'work' }))];

                // Kranktage hinzufügen (nur wenn nicht schon als Arbeitstag vorhanden)
                (m.sickDates || []).forEach(s => {
                  if (!allDays.find(d => d.date === s.date)) {
                    const weekday = getWeekday(s.date);
                    allDays.push({ date: s.date, dayType: 'sick', kommen: null, gehen: null, presentMinutes: 0, breakMinutes: 0, nettoMinutes: 0, overtimeMinutes: 0, isSaturday: weekday === 'Sa', isSunday: weekday === 'So' });
                  } else {
                    const existing = allDays.find(d => d.date === s.date);
                    if (existing) existing.dayType = 'sick+work';
                  }
                });

                // Urlaubstage hinzufügen
                (m.vacationDates || []).forEach(v => {
                  if (!allDays.find(d => d.date === v.date)) {
                    const weekday = getWeekday(v.date);
                    allDays.push({ date: v.date, dayType: 'vacation', kommen: null, gehen: null, presentMinutes: 0, breakMinutes: 0, nettoMinutes: 0, overtimeMinutes: 0, isSaturday: weekday === 'Sa', isSunday: weekday === 'So' });
                  } else {
                    const existing = allDays.find(d => d.date === v.date);
                    if (existing) existing.dayType = 'vacation+work';
                  }
                });

                // Sortieren nach Datum
                allDays.sort((a, b) => {
                  const [ad, am, ay] = a.date.split('.').map(Number);
                  const [bd, bm, by] = b.date.split('.').map(Number);
                  return (ay - by) || (am - bm) || (ad - bd);
                });

                return (
                <div className="border-t border-gray-100">
                  {/* Desktop */}
                  <div className="hidden sm:block divide-y divide-gray-50">
                    {allDays.map((day, i) => {
                      const weekday = getWeekday(day.date);
                      const isWeekend = day.isSaturday || day.isSunday;
                      return (
                        <div key={i} className={`flex items-center px-5 py-2.5 gap-3 text-sm ${isWeekend ? 'bg-amber-50/50' : ''}`}>
                          <div className="w-20 flex-shrink-0">
                            <span className={`font-medium ${isWeekend ? 'text-amber-700' : 'text-gray-700'}`}>
                              {weekday} {day.date.substring(0, 5)}
                            </span>
                          </div>
                          {day.dayType === 'sick' ? (
                            <div className="flex items-center gap-2 flex-1">
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                <ThermometerSun className="w-3 h-3" /> Krank
                              </span>
                            </div>
                          ) : day.dayType === 'vacation' ? (
                            <div className="flex items-center gap-2 flex-1">
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                <Palmtree className="w-3 h-3" /> Urlaub
                              </span>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-1.5 w-32 flex-shrink-0 text-gray-500">
                                <Clock className="w-3.5 h-3.5 text-gray-400" />
                                {day.kommen || '--:--'} – {day.gehen || '--:--'}
                              </div>
                              <div className="w-16 flex-shrink-0 text-gray-400">{formatMinutesShort(day.presentMinutes)}</div>
                          {day.dayType === 'sick+work' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-700">
                              <ThermometerSun className="w-2.5 h-2.5" /> Krank
                            </span>
                          )}
                          {day.dayType === 'vacation+work' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700">
                              <Palmtree className="w-2.5 h-2.5" /> Urlaub
                            </span>
                          )}
                          {day.breakMinutes > 0 ? (
                            <div className="flex items-center gap-1 w-16 flex-shrink-0 whitespace-nowrap">
                              <Coffee className="w-3 h-3 text-gray-300" />
                              <span className="text-xs text-gray-400">-{day.breakMinutes}m</span>
                            </div>
                          ) : (
                            <div className="w-16 flex-shrink-0" />
                          )}
                          <div className="w-20 flex-shrink-0 font-medium text-gray-700 whitespace-nowrap">= {formatMinutesShort(day.nettoMinutes)}</div>
                          {day.project && (
                            <div className="flex-1 text-xs text-gray-400 truncate">{day.project}</div>
                          )}
                          <div className="text-right flex-shrink-0">
                            {day.overtimeMinutes > 0 ? (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                                isWeekend ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {isWeekend ? <Sun className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                                +{formatMinutesShort(day.overtimeMinutes)}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Mobile */}
                  <div className="sm:hidden divide-y divide-gray-50">
                    {allDays.map((day, i) => {
                      const weekday = getWeekday(day.date);
                      const isWeekend = day.isSaturday || day.isSunday;
                      return (
                        <div key={i} className={`px-4 py-2.5 ${isWeekend ? 'bg-amber-50/50' : ''}`}>
                          <div className="flex items-center justify-between mb-0.5">
                            <span className={`text-sm font-medium ${isWeekend ? 'text-amber-700' : 'text-gray-700'}`}>
                              {weekday} {day.date.substring(0, 5)}
                            </span>
                            {day.overtimeMinutes > 0 ? (
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                isWeekend ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                              }`}>+{formatMinutesShort(day.overtimeMinutes)}</span>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </div>
                          {day.dayType === 'sick' ? (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-yellow-100 text-yellow-700">
                                <ThermometerSun className="w-3 h-3" /> Krank
                              </span>
                            </div>
                          ) : day.dayType === 'vacation' ? (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">
                                <Palmtree className="w-3 h-3" /> Urlaub
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              {(day.dayType === 'sick+work' || day.dayType === 'vacation+work') && (
                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                                  day.dayType === 'sick+work' ? 'bg-yellow-100 text-yellow-700' : 'bg-purple-100 text-purple-700'
                                }`}>
                                  {day.dayType === 'sick+work' ? 'K' : 'U'}
                                </span>
                              )}
                              <span>{day.kommen || '--:--'} – {day.gehen || '--:--'}</span>
                              <span>·</span>
                              <span>{formatMinutesShort(day.presentMinutes)}</span>
                              {day.breakMinutes > 0 && (
                                <><span>·</span><span className="text-gray-400">-{day.breakMinutes}m</span></>
                              )}
                              <span>·</span>
                              <span className="font-medium text-gray-700">= {formatMinutesShort(day.nettoMinutes)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Monats-Summe */}
                  <div className="bg-gray-50 border-t border-gray-200 px-5 py-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      {m.daysPresent} Werktage{m.weekendDays > 0 ? ` + ${m.weekendDays} Sa/So` : ''}
                      {m.sickDays > 0 ? ` · ${m.sickDays} krank` : ''}
                      {m.vacationDays > 0 ? ` · ${m.vacationDays} Urlaub` : ''}
                    </span>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-500">Ist: <strong>{formatMinutes(m.actualMinutes)}</strong></span>
                      <span className="text-gray-400">Soll: {formatMinutes(m.targetMinutes)}</span>
                      {m.overtimeMinutes > 0 && (
                        <span className="font-semibold text-green-600">+{formatMinutes(m.overtimeMinutes)} ÜS</span>
                      )}
                    </div>
                  </div>
                </div>
                ); })()}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ========== STAMMBLATT TAB ==========
const GENDER_OPTIONS = [
  { value: 'männlich', label: 'Männlich' },
  { value: 'weiblich', label: 'Weiblich' },
  { value: 'divers', label: 'Divers' },
];

const MARITAL_OPTIONS = [
  { value: 'ledig', label: 'Ledig' },
  { value: 'verheiratet', label: 'Verheiratet' },
  { value: 'geschieden', label: 'Geschieden' },
  { value: 'verwitwet', label: 'Verwitwet' },
  { value: 'eingetragene Lebenspartnerschaft', label: 'Eingetragene Lebenspartnerschaft' },
];

const TAX_CLASS_OPTIONS = [
  { value: '1', label: 'I' },
  { value: '2', label: 'II' },
  { value: '3', label: 'III' },
  { value: '4', label: 'IV' },
  { value: '5', label: 'V' },
  { value: '6', label: 'VI' },
];

const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b border-gray-200 pb-2 mb-4">{children}</h3>
);

const FieldRow = ({ label, children, span = 1 }) => (
  <div className={span === 2 ? 'col-span-2' : ''}>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    {children}
  </div>
);

const CheckboxField = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 cursor-pointer py-2">
    <input
      type="checkbox"
      checked={!!checked}
      onChange={onChange}
      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
    />
    <span className="text-sm text-gray-700">{label}</span>
  </label>
);

const StammblattTab = ({ userId }) => {
  const queryClient = useQueryClient();
  const { data: serverData, isLoading } = useStammblatt(userId);
  const [localData, setLocalData] = useState<Record<string, any> | null>(null);

  // Use local edits if present, otherwise fall back to server data
  const data = localData ?? serverData ?? {};

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, any>) => usersApi.updateStammblatt(userId, payload).then(r => r.data),
    onSuccess: (updated) => {
      queryClient.setQueryData(['users', userId, 'stammblatt'], updated);
      setLocalData(null);
      toast.success('Stammblatt gespeichert');
    },
    onError: (err: any) => {
      toast.error(err.message || 'Fehler beim Speichern');
    },
  });

  const handleSave = () => saveMutation.mutate(data);

  const set = (field: string, value: any) => setLocalData(prev => ({ ...(prev ?? serverData ?? {}), [field]: value }));

  if (isLoading) return <Spinner.Center />;

  return (
    <div className="space-y-6 print:space-y-2" id="stammblatt-content">
      {/* Print header — only visible when printing */}
      <div className="hidden print:block print:mb-2">
        <h1 className="text-xl font-bold text-center">Personalstammblatt</h1>
        <p className="text-center text-sm text-gray-600">{data.name || ''} {data.personal_number ? `(${data.personal_number})` : ''}</p>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" icon={FileText} onClick={() => window.open(`/api/users/${userId}/stammblatt/pdf`, '_blank')}>
          PDF Export
        </Button>
        <Button variant="primary" icon={Save} onClick={handleSave} loading={saveMutation.isPending}>
          Speichern
        </Button>
      </div>

      {/* Persönliche Daten */}
      <Card>
        <div className="p-5 print:p-2">
          <SectionTitle>Persönliche Daten</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 print:grid-cols-3 print:gap-1">
            <FieldRow label="Geburtsdatum">
              <Input type="date" value={data.birthdate || ''} onChange={e => set('birthdate', e.target.value)} />
            </FieldRow>
            <FieldRow label="Geburtsname">
              <Input value={data.birth_name || ''} onChange={e => set('birth_name', e.target.value)} placeholder="Falls abweichend" />
            </FieldRow>
            <FieldRow label="Geburtsland">
              <Input value={data.birth_country || ''} onChange={e => set('birth_country', e.target.value)} />
            </FieldRow>
            <FieldRow label="Geburtsort">
              <Input value={data.birth_city || ''} onChange={e => set('birth_city', e.target.value)} />
            </FieldRow>
            <FieldRow label="Geschlecht">
              <Select value={data.gender || ''} onChange={e => set('gender', e.target.value)} options={GENDER_OPTIONS} placeholder="Bitte wählen" />
            </FieldRow>
            <FieldRow label="Familienstand">
              <Select value={data.marital_status || ''} onChange={e => set('marital_status', e.target.value)} options={MARITAL_OPTIONS} placeholder="Bitte wählen" />
            </FieldRow>
            <FieldRow label="Staatsangehörigkeit">
              <Input value={data.nationality || ''} onChange={e => set('nationality', e.target.value)} />
            </FieldRow>
          </div>
        </div>
      </Card>

      {/* Anschrift */}
      <Card>
        <div className="p-5 print:p-2">
          <SectionTitle>Anschrift</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FieldRow label="Straße + Hausnummer" span={2}>
              <Input value={data.street || ''} onChange={e => set('street', e.target.value)} />
            </FieldRow>
            <div />
            <FieldRow label="PLZ">
              <Input value={data.zip_code || ''} onChange={e => set('zip_code', e.target.value)} maxLength={5} />
            </FieldRow>
            <FieldRow label="Ort" span={2}>
              <Input value={data.city || ''} onChange={e => set('city', e.target.value)} />
            </FieldRow>
          </div>
        </div>
      </Card>

      {/* Sozialversicherung & Steuern */}
      <Card>
        <div className="p-5 print:p-2">
          <SectionTitle>Sozialversicherung & Steuern</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FieldRow label="SV-Nummer">
              <Input value={data.sv_number || ''} onChange={e => set('sv_number', e.target.value)} placeholder="z.B. 12345678A012" />
            </FieldRow>
            <FieldRow label="Krankenkasse">
              <Input value={data.health_insurance || ''} onChange={e => set('health_insurance', e.target.value)} />
            </FieldRow>
            <FieldRow label="Steuer-Identifikationsnummer">
              <Input value={data.tax_id || ''} onChange={e => set('tax_id', e.target.value)} placeholder="11-stellig" />
            </FieldRow>
            <FieldRow label="Steuerklasse">
              <Select value={data.tax_class || ''} onChange={e => set('tax_class', e.target.value)} options={TAX_CLASS_OPTIONS} placeholder="Bitte wählen" />
            </FieldRow>
            <FieldRow label="Konfession">
              <Input value={data.denomination || ''} onChange={e => set('denomination', e.target.value)} placeholder="z.B. keine, ev, rk" />
            </FieldRow>
            <div className="flex gap-6">
              <FieldRow label="Kinder">
                <Select
                  value={data.has_children ? '1' : '0'}
                  onChange={e => set('has_children', parseInt(e.target.value))}
                  options={[{ value: '0', label: 'Nein' }, { value: '1', label: 'Ja' }]}
                />
              </FieldRow>
              <FieldRow label="Kinderfreibetrag">
                <Input inputMode="decimal" value={data.child_allowance ?? ''} onChange={e => set('child_allowance', e.target.value)} placeholder="0" />
              </FieldRow>
            </div>
          </div>
        </div>
      </Card>

      {/* Beschäftigung */}
      <Card>
        <div className="p-5 print:p-2">
          <SectionTitle>Beschäftigung</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FieldRow label="Eintrittsdatum">
              <Input type="date" value={data.entry_date || ''} onChange={e => set('entry_date', e.target.value)} />
            </FieldRow>
            <FieldRow label="Tätigkeit">
              <Input value={data.job_title || ''} onChange={e => set('job_title', e.target.value)} placeholder="z.B. Bauleiter, Elektriker" />
            </FieldRow>
            <FieldRow label="Festbezug / Stundenlohn (€)">
              <Input inputMode="decimal" value={data.hourly_rate ?? ''} onChange={e => set('hourly_rate', e.target.value)} placeholder="0.00" />
            </FieldRow>
          </div>
          <div className="flex flex-wrap gap-6 mt-3">
            <CheckboxField label="Geringfügig beschäftigt" checked={data.is_minijob} onChange={e => set('is_minijob', e.target.checked ? 1 : 0)} />
            <CheckboxField label="Weitere Beschäftigungen" checked={data.has_other_employment} onChange={e => set('has_other_employment', e.target.checked ? 1 : 0)} />
          </div>
        </div>
      </Card>

      {/* Firmenwagen */}
      <Card>
        <div className="p-5 print:p-2">
          <SectionTitle>Firmenwagen</SectionTitle>
          <div className="flex flex-wrap gap-6">
            <CheckboxField label="Firmenwagen vorhanden" checked={data.has_company_car} onChange={e => set('has_company_car', e.target.checked ? 1 : 0)} />
            {!!data.has_company_car && (
              <>
                <CheckboxField label="Nutzungsverbot" checked={data.car_usage_ban} onChange={e => set('car_usage_ban', e.target.checked ? 1 : 0)} />
                <CheckboxField label="Privatnutzung" checked={data.car_private_use} onChange={e => set('car_private_use', e.target.checked ? 1 : 0)} />
                <CheckboxField label="Fahrtenbuch" checked={data.car_logbook} onChange={e => set('car_logbook', e.target.checked ? 1 : 0)} />
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Bankverbindung */}
      <Card>
        <div className="p-5 print:p-2">
          <SectionTitle>Bankverbindung</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FieldRow label="Bank">
              <Input value={data.bank_name || ''} onChange={e => set('bank_name', e.target.value)} />
            </FieldRow>
            <FieldRow label="IBAN">
              <Input value={data.iban || ''} onChange={e => set('iban', e.target.value)} placeholder="DE..." className="font-mono" />
            </FieldRow>
            <FieldRow label="BIC">
              <Input value={data.bic || ''} onChange={e => set('bic', e.target.value)} className="font-mono" />
            </FieldRow>
          </div>
        </div>
      </Card>

      {/* VWL */}
      <Card>
        <div className="p-5 print:p-2">
          <SectionTitle>Vermögenswirksame Leistungen (VWL)</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FieldRow label="AN-Anteil (€)">
              <Input inputMode="decimal" value={data.vwl_employee_share ?? ''} onChange={e => set('vwl_employee_share', e.target.value)} placeholder="0.00" />
            </FieldRow>
            <FieldRow label="AG-Anteil (€)">
              <Input inputMode="decimal" value={data.vwl_employer_share ?? ''} onChange={e => set('vwl_employer_share', e.target.value)} placeholder="0.00" />
            </FieldRow>
            <FieldRow label="VWL-Bank">
              <Input value={data.vwl_bank || ''} onChange={e => set('vwl_bank', e.target.value)} />
            </FieldRow>
            <FieldRow label="VWL-IBAN">
              <Input value={data.vwl_iban || ''} onChange={e => set('vwl_iban', e.target.value)} className="font-mono" />
            </FieldRow>
            <FieldRow label="VWL-BIC">
              <Input value={data.vwl_bic || ''} onChange={e => set('vwl_bic', e.target.value)} className="font-mono" />
            </FieldRow>
          </div>
          <div className="flex flex-wrap gap-6 mt-3">
            <CheckboxField label="Lohnoptimierung" checked={data.salary_optimization} onChange={e => set('salary_optimization', e.target.checked ? 1 : 0)} />
            <CheckboxField label="bAV" checked={data.bav} onChange={e => set('bav', e.target.checked ? 1 : 0)} />
            <CheckboxField label="HEK" checked={data.hek} onChange={e => set('hek', e.target.checked ? 1 : 0)} />
          </div>
        </div>
      </Card>

      <div className="flex justify-end pb-4 print:hidden">
        <Button variant="primary" icon={Save} onClick={handleSave} loading={saveMutation.isPending}>
          Speichern
        </Button>
      </div>
    </div>
  );
};

// ========== HAUPTKOMPONENTE ==========
const TABS = [
  { key: 'zeiterfassung', label: 'Zeiterfassung', icon: Clock },
  { key: 'stammblatt', label: 'Stammblatt', icon: FileText },
];

const MitarbeiterDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'zeiterfassung');

  const { data: user, isLoading, isError } = useUser(id);

  if (isLoading) return <Spinner.Center />;
  if (isError) {
    toast.error('Fehler beim Laden des Mitarbeiters');
    navigate('/admin/mitarbeiter');
    return null;
  }
  if (!user) return null;

  return (
    <div>
      <div className="print:hidden">
        <PageHeader
          title={user.name}
          description={`${user.role === 'bauleiter' ? 'Bauleiter' : user.role === 'admin' ? 'Admin' : 'Mitarbeiter'} · ${user.employment_type || 'VZ'}`}
          showDate={false}
          actions={
            <Link to="/admin/mitarbeiter">
              <Button variant="outline" icon={ArrowLeft}>Zurück</Button>
            </Link>
          }
        />
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6 print:hidden">
        <nav className="flex gap-1">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'zeiterfassung' && <DetailsTab userId={id} />}
      {activeTab === 'stammblatt' && <StammblattTab userId={id} />}
    </div>
  );
};

export default MitarbeiterDetail;
